/**
 * Admin Dashboard i18n
 * Supports English (en) and Arabic (ar).
 */

export type AdminLang = 'en' | 'ar';

export const ADMIN_LABELS = {
  en: {
    dir: 'ltr' as const,
    brandTitle: 'Dubai Police',
    brandSub: 'Admin Dashboard',
    navConversations: 'Conversations',
    navKnowledgeBase: 'Knowledge Base',
    logout: 'Sign Out',
    langLabel: 'Language',
    // Conversations page
    conversationsTitle: 'Conversations',
    searchPlaceholder: 'Search',
    noConversations: 'No conversations found.',
    loading: 'Loading…',
    // Categories (labels match Figma design exactly)
    catPolice: 'Police',
    catAmbulance: 'Ambulance',
    catCivilDefense: 'Civil Defence',
    catInquiry: 'Inquiries',
    catCallBack: 'Call back',
    // Conversation thread
    callerName: 'Caller Name',
    phoneNumber: 'Phone Number',
    date: 'Date',
    time: 'Time',
    language: 'Language',
    location: 'Location',
    locationNone: 'Location not available',
    viewOnMap: 'View on Map ↗',
    incidentNumber: 'Incident Number',
    incidentSummary: 'Incident Summary',
    noMessages: 'No messages recorded for this session.',
    caller: 'Caller',
    bot: 'Dubai Police Bot',
    hideDetails: '▲ Hide details',
    showDetails: '▼ Caller details',
    markResolved: '✓ Mark Resolved',
    callBack: 'Call Back',
    // KB page
    kbTitle: 'Knowledge Base',
    kbSearch: 'Search questions or answers…',
    kbAddBtn: '+ Add Q&A',
    kbNoItems: 'No Q&As found.',
    kbEntries: (n: number) => `${n} entries`,
    // FAQ Editor
    editorAddTitle: 'Add New Q&A',
    editorEditTitle: 'Edit Q&A',
    questionLabel: 'Question',
    answerLabel: 'Answer',
    questionPlaceholder: 'Type the question…',
    answerPlaceholder: 'Type the answer…',
    translateBtn: '🌐 Auto-Translate',
    translating: 'Translating…',
    viewTranslations: 'View / edit translations',
    hideTranslations: 'Hide translations',
    categoryLabel: 'Category',
    saveBtn: 'Save Changes',
    addBtn: 'Add Q&A',
    saving: 'Saving…',
    cancel: 'Cancel',
    deleteConfirmTitle: 'Delete this Q&A?',
    deleteConfirmBody: 'This action cannot be undone.',
    deleteBtn: 'Delete',
    editorRequired: 'Question and answer are required.',
    editorSaveFail: 'Failed to save. Please try again.',
    // Lang labels
    langEn: 'English',
    langAr: 'Arabic',
    langUr: 'Urdu',
    // Status
    statusOpen: 'Open',
    statusResolved: 'Resolved',
    statusPendingHuman: 'Pending Human Contact',
    statusActive: 'Active',
    // Map & new UI
    showOnMap: 'Show on map',
    resetFilter: 'Reset',
    filterBtn: 'Filter',
    callDetails: 'Call Details',
    allTab: 'All',
    noLocation: 'No location',
    mapLoadError: 'Map failed to load',
    callBackAction: 'Call back',
    markAsResolved: 'Mark as resolved',
    closeModal: 'Close',
    pinPopupResolve: 'Mark as resolved',
  },
  ar: {
    dir: 'rtl' as const,
    brandTitle: 'شرطة دبي',
    brandSub: 'لوحة تحكم المشرف',
    navConversations: 'المحادثات',
    navKnowledgeBase: 'قاعدة المعرفة',
    logout: 'تسجيل الخروج',
    langLabel: 'اللغة',
    // Conversations page
    conversationsTitle: 'المحادثات',
    searchPlaceholder: 'بحث',
    noConversations: 'لا توجد محادثات.',
    loading: 'جاري التحميل…',
    // Categories
    catPolice: 'شرطة دبي',
    catAmbulance: 'الإسعاف',
    catCivilDefense: 'الدفاع المدني',
    catInquiry: 'الاستفسارات',
    catCallBack: 'طلب معاودة الاتصال',
    // Conversation thread
    callerName: 'اسم المتصل',
    phoneNumber: 'رقم الهاتف',
    date: 'التاريخ',
    time: 'الوقت',
    language: 'اللغة',
    location: 'الموقع',
    locationNone: 'الموقع غير متاح',
    viewOnMap: 'عرض على الخريطة ↗',
    incidentNumber: 'رقم البلاغ',
    incidentSummary: 'ملخص الحادثة',
    noMessages: 'لا توجد رسائل مسجلة لهذه الجلسة.',
    caller: 'المتصل',
    bot: 'مساعد شرطة دبي',
    hideDetails: '▲ إخفاء التفاصيل',
    showDetails: '▼ تفاصيل المتصل',
    markResolved: '✓ تم حل المشكلة',
    callBack: 'معاودة الاتصال',
    // KB page
    kbTitle: 'قاعدة المعرفة',
    kbSearch: 'ابحث في الأسئلة والأجوبة…',
    kbAddBtn: '+ إضافة سؤال وجواب',
    kbNoItems: 'لا توجد أسئلة وأجوبة.',
    kbEntries: (n: number) => `${n} إدخالات`,
    // FAQ Editor
    editorAddTitle: 'إضافة سؤال وجواب جديد',
    editorEditTitle: 'تعديل السؤال والجواب',
    questionLabel: 'السؤال',
    answerLabel: 'الجواب',
    questionPlaceholder: 'اكتب السؤال…',
    answerPlaceholder: 'اكتب الجواب…',
    translateBtn: '🌐 ترجمة تلقائية',
    translating: 'جاري الترجمة…',
    viewTranslations: 'عرض / تعديل الترجمات',
    hideTranslations: 'إخفاء الترجمات',
    categoryLabel: 'الفئة',
    saveBtn: 'حفظ التغييرات',
    addBtn: 'إضافة سؤال وجواب',
    saving: 'جاري الحفظ…',
    cancel: 'إلغاء',
    deleteConfirmTitle: 'حذف هذا السؤال والجواب؟',
    deleteConfirmBody: 'لا يمكن التراجع عن هذا الإجراء.',
    deleteBtn: 'حذف',
    editorRequired: 'السؤال والجواب مطلوبان.',
    editorSaveFail: 'فشل الحفظ. يرجى المحاولة مرة أخرى.',
    // Lang labels
    langEn: 'الإنجليزية',
    langAr: 'العربية',
    langUr: 'الأردية',
    // Status
    statusOpen: 'مفتوح',
    statusResolved: 'محلول',
    statusPendingHuman: 'في انتظار التواصل البشري',
    statusActive: 'نشط',
    // Map & new UI
    showOnMap: 'عرض على الخريطة',
    resetFilter: 'إعادة تعيين',
    filterBtn: 'تصفية',
    callDetails: 'تفاصيل المكالمة',
    allTab: 'الكل',
    noLocation: 'لا يوجد موقع',
    mapLoadError: 'فشل تحميل الخريطة',
    callBackAction: 'معاودة الاتصال',
    markAsResolved: 'تم الحل',
    closeModal: 'إغلاق',
    pinPopupResolve: 'تم الحل',
  },
} as const;

export function useAdminLabels(lang: AdminLang) {
  return ADMIN_LABELS[lang];
}
